import { BASE_URL } from "../../App_Settings/app_settings";
import { getUserIdFromLocalStorage } from "../../API_Endpoints/APIEndpoints";

// Invoice data interface
export interface InvoiceData {
  id: string;
  organizationId: string;
  organizationName: string;
  invoiceNumber: string;
  invoiceDate: string;
  startDate: string;
  endDate: string;
  totalPages: number;
  pricePerPage: number; // in pence
  totalAmount: number; // in pounds
  status: string; // 'paid', 'pending', 'overdue'
  pdfUrl?: string;
  createdBy: string;
  createdDate: string;
}

export interface PageUsageData {
  organizationId: string;
  organizationName: string;
  totalPages: number;
  startDate: string;
  endDate: string;
}

// Pricing constants
export const PRICE_PER_PAGE_PENCE = 10; // 10 pence per page
export const PRICE_PER_PAGE_POUNDS = PRICE_PER_PAGE_PENCE / 100; // 0.10 pounds

/**
 * Get page usage for an organization within a date range
 */
export const getPageUsageByOrganization = async (
  organizationId: string,
  startDate?: string,
  endDate?: string
): Promise<PageUsageData> => {
  try {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);

    const response = await fetch(
      `${BASE_URL}/Invoice/page-usage/${organizationId}?${params.toString()}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${getUserIdFromLocalStorage().token}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch page usage: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching page usage:", error);
    throw error;
  }
};

/**
 * Get all invoices for an organization
 */
export const getInvoicesByOrganization = async (organizationId: string): Promise<InvoiceData[]> => {
  try {
    const response = await fetch(`${BASE_URL}/Invoice/organization/${organizationId}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getUserIdFromLocalStorage().token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch invoices: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching invoices:", error);
    throw error;
  }
};

/**
 * Get all invoices (super admin only)
 */
export const getAllInvoices = async (): Promise<InvoiceData[]> => {
  try {
    const response = await fetch(`${BASE_URL}/Invoice/all`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getUserIdFromLocalStorage().token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch all invoices: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching all invoices:", error);
    throw error;
  }
};

/**
 * Generate an invoice for an organization
 */
export const generateInvoice = async (
  organizationId: string,
  startDate: string,
  endDate: string
): Promise<InvoiceData> => {
  try {
    const userData = getUserIdFromLocalStorage();
    const invoiceData = {
      organizationId,
      startDate,
      endDate,
      pricePerPage: PRICE_PER_PAGE_PENCE,
      createdBy: userData.email || userData.userName,
    };

    const response = await fetch(`${BASE_URL}/Invoice/generate`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${userData.token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(invoiceData),
    });

    if (!response.ok) {
      throw new Error(`Failed to generate invoice: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error generating invoice:", error);
    throw error;
  }
};

/**
 * Get a specific invoice by ID
 */
export const getInvoiceById = async (invoiceId: string): Promise<InvoiceData> => {
  try {
    const response = await fetch(`${BASE_URL}/Invoice/${invoiceId}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getUserIdFromLocalStorage().token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch invoice: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching invoice:", error);
    throw error;
  }
};

/**
 * Download invoice PDF
 */
export const downloadInvoicePDF = async (invoiceId: string): Promise<void> => {
  try {
    const response = await fetch(`${BASE_URL}/Invoice/${invoiceId}/pdf`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getUserIdFromLocalStorage().token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to download invoice PDF: ${response.status}`);
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `invoice-${invoiceId}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error downloading invoice PDF:", error);
    throw error;
  }
};

/**
 * Calculate total amount for given pages
 */
export const calculateAmount = (totalPages: number): number => {
  return totalPages * PRICE_PER_PAGE_POUNDS;
};

/**
 * Format amount in pounds with currency symbol
 */
export const formatAmount = (amount: number): string => {
  return `£${amount.toFixed(2)}`;
};

/**
 * Update invoice status
 */
export const updateInvoiceStatus = async (
  invoiceId: string,
  status: string
): Promise<InvoiceData> => {
  try {
    const response = await fetch(`${BASE_URL}/Invoice/${invoiceId}/status`, {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${getUserIdFromLocalStorage().token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ status }),
    });

    if (!response.ok) {
      throw new Error(`Failed to update invoice status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error updating invoice status:", error);
    throw error;
  }
};

/**
 * Delete an invoice
 */
export const deleteInvoice = async (invoiceId: string): Promise<void> => {
  try {
    const response = await fetch(`${BASE_URL}/Invoice/${invoiceId}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${getUserIdFromLocalStorage().token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to delete invoice: ${response.status}`);
    }
  } catch (error) {
    console.error("Error deleting invoice:", error);
    throw error;
  }
};
